import {
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { type KeyboardEvent, useContext, useEffect, useState } from 'react';
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
} from 'firebase/auth';
import { FaGoogle } from 'react-icons/fa';
import type { FirebaseError } from 'firebase/app';
import { useMobileFormModal } from '@/hooks/useMobileFormModal';
import { errorToastOptions, getAuthErrorMessage, hasGoogleProvider, hasPasswordProvider } from '@/helpers';
import { useToast } from '@chakra-ui/react';
import { FirebaseContext } from '@/session/FirebaseContext';

const GAUTH_PROVIDER = new GoogleAuthProvider().setCustomParameters({ prompt: 'select_account' });

export function ReauthModal(props: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
  title?: string;
}) {
  const ctx = useContext(FirebaseContext);
  const toast = useToast();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const { isMobile, modalSize, isCentered, scrollBehavior, contentProps, footerPb, titleFontSize } =
    useMobileFormModal();

  const user = ctx.user;
  const canUsePassword = user ? hasPasswordProvider(user) : false;
  const canUseGoogle = user ? hasGoogleProvider(user) : false;

  useEffect(() => {
    if (props.isOpen) {
      setPassword('');
      setShowPassword(false);
      setIsBusy(false);
    }
  }, [props.isOpen]);

  async function handleSuccess() {
    try {
      await props.onSuccess();
      props.onClose();
    } catch (err) {
      toast(errorToastOptions('Action failed', getAuthErrorMessage(err as FirebaseError)));
    }
  }

  async function reauthWithPassword() {
    if (!user?.email) return;
    setIsBusy(true);
    try {
      const cred = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, cred);
      await handleSuccess();
    } catch (err) {
      toast(errorToastOptions('Reauthentication failed', getAuthErrorMessage(err as FirebaseError)));
    } finally {
      setIsBusy(false);
    }
  }

  async function reauthWithGoogle() {
    if (!user) return;
    setIsBusy(true);
    try {
      await reauthenticateWithPopup(user, GAUTH_PROVIDER);
      await handleSuccess();
    } catch (err) {
      const firebaseErr = err as FirebaseError;
      if (firebaseErr.code !== 'auth/popup-closed-by-user') {
        toast(errorToastOptions('Reauthentication failed', getAuthErrorMessage(firebaseErr)));
      }
    } finally {
      setIsBusy(false);
    }
  }

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      isCentered={isCentered}
      size={modalSize}
      scrollBehavior={scrollBehavior}
    >
      <ModalOverlay />
      <ModalContent {...contentProps}>
        <ModalHeader>
          <Heading fontSize={titleFontSize}>{props.title ?? 'Confirm your identity'}</Heading>
        </ModalHeader>
        <ModalCloseButton mt={2} mr={2} />
        <ModalBody px={{ base: 4, md: 6 }}>
          <Stack spacing={4}>
            <Text fontSize='sm'>Please sign in again to continue.</Text>
            {canUsePassword && (
              <FormControl>
                <FormLabel>Current password</FormLabel>
                <InputGroup>
                  <Input
                    size={isMobile ? 'lg' : 'md'}
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete='current-password'
                    onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter' && password.length > 0) {
                        void reauthWithPassword();
                      }
                    }}
                  />
                  <InputRightElement width='4.5rem'>
                    <Button h='1.75rem' size='sm' onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? 'Hide' : 'Show'}
                    </Button>
                  </InputRightElement>
                </InputGroup>
              </FormControl>
            )}
            {canUseGoogle && (
              <Button
                w='full'
                leftIcon={<FaGoogle color='white' />}
                backgroundColor={useColorModeValue('red.500', 'red.500')}
                color='white'
                _hover={{}}
                isLoading={isBusy}
                onClick={() => void reauthWithGoogle()}
              >
                Continue with Google
              </Button>
            )}
          </Stack>
        </ModalBody>
        {canUsePassword && (
          <ModalFooter pb={footerPb}>
            <Button
              w={{ base: 'full', md: 'auto' }}
              isLoading={isBusy}
              isDisabled={password.length === 0}
              colorScheme='green'
              onClick={() => void reauthWithPassword()}
            >
              Confirm
            </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
}
